// Surface CLIENT de « La Mine aux briques » (Lot A) : badge pièces réutilisable, panneau Quêtes
// (DOM, même ossature que le panneau Réglages de home-ui.ts) et toasts de complétion. TOUT est
// hors simulation : on lit l'état économique via l'API (server = seule autorité) et on l'affiche.
// Aucun accès au hash d'état, aucun Math.random dans du rendu simulé.
import type { ChallengeDto, DailyGrantDto, MineStateResponse, QuestDto, ScoreResponse, WalletDto } from '../shared/api-types'
import { STREAK_BRICK_STEP, STREAK_GOLDEN_STEP } from '../shared/economy'
import { apiMineState } from './api'
import { mountDuelSection } from './duel-ui'
import { playEvents } from './audio'

/** Vrai si le boost « Filon ×2 » est ACTIF (boost_until dans le futur). `boostUntil` est un datetime
 *  SQL UTC « YYYY-MM-DD HH:MM:SS » : on le relit en UTC (T…Z) pour le comparer à maintenant. Dégrade
 *  proprement : null/undefined/illisible -> false (aucun marqueur affiché). Lecture seule. */
export function boostActive(boostUntil: string | null | undefined): boolean {
  if (!boostUntil) return false
  const t = Date.parse(boostUntil.replace(' ', 'T') + 'Z')
  return Number.isFinite(t) && t > Date.now()
}

/** Petite pastille « 🪙 N » posée sur l'accueil. IDEMPOTENT : retire une éventuelle pastille
 *  déjà présente dans `parent` avant d'en monter une neuve (l'accueil est reconstruit à chaque
 *  visite ; sans ce nettoyage les badges s'empileraient au fil d'une session famille). */
export function mountCoinBadge(parent: HTMLElement, wallet: WalletDto): HTMLElement {
  parent.querySelector('.hm-coins')?.remove()
  const badge = document.createElement('div')
  badge.className = 'hm-coins'
  // Boost « Filon ×2 » : indicateur doré collé aux pièces tant que boost_until est dans le futur.
  const boosted = boostActive(wallet.boostUntil)
  badge.setAttribute(
    'aria-label',
    `${wallet.coins} pièces${boosted ? ' (bonus ×2 actif)' : ''}, ${wallet.bricks} briques, ${wallet.shards} éclats`,
  )
  // Trois devises côte à côte (🪙 pièces, 🧱 briques, 💎 éclats), lecture seule : chaque paire
  // icône+nombre est groupée dans un .hm-coins-item pour rester lisible (espace serré à l'intérieur,
  // large entre devises). Les soldes viennent du serveur (wallet), jamais recalculés côté client.
  const item = (icoTxt: string, value: number): HTMLElement => {
    const wrap = document.createElement('span')
    wrap.className = 'hm-coins-item'
    const ico = document.createElement('span')
    ico.className = 'hm-coins-ico'
    ico.textContent = icoTxt
    const n = document.createElement('span')
    n.className = 'hm-coins-n'
    n.textContent = String(value)
    wrap.append(ico, n)
    return wrap
  }
  const coinsItem = item('🪙', wallet.coins)
  if (boosted) {
    const x2 = document.createElement('span')
    x2.className = 'hm-coins-x2'
    x2.textContent = '×2'
    x2.title = 'Filon ×2 actif : pièces gagnées doublées'
    coinsItem.appendChild(x2)
  }
  badge.append(coinsItem, item('🧱', wallet.bricks), item('💎', wallet.shards))
  parent.appendChild(badge)
  return badge
}

// Sections du panneau, dans l'ordre d'affichage (le serveur renvoie déjà jour puis semaine puis vie).
const PERIOD_TITLES: { period: QuestDto['period']; title: string }[] = [
  { period: 'day', title: "Aujourd'hui" },
  { period: 'week', title: 'Cette semaine' },
  { period: 'life', title: 'À vie' },
]

/** Construit une ligne de quête : libellé + gain, barre de progression n/target, ✓ si accomplie. */
function questRow(q: QuestDto): HTMLElement {
  const row = document.createElement('div')
  row.className = q.done ? 'hq-row hq-row-done' : 'hq-row'

  const top = document.createElement('div')
  top.className = 'hq-row-top'
  const label = document.createElement('span')
  label.className = 'hq-label'
  label.textContent = q.label
  const gain = document.createElement('span')
  gain.className = 'hq-gain'
  gain.textContent = `+${q.coins} 🪙`
  top.append(label, gain)

  const bar = document.createElement('div')
  bar.className = 'hq-bar'
  const fill = document.createElement('div')
  fill.className = 'hq-bar-fill'
  const pct = q.target > 0 ? Math.min(100, Math.round((q.n / q.target) * 100)) : 0
  fill.style.width = `${pct}%`
  bar.appendChild(fill)

  const prog = document.createElement('div')
  prog.className = 'hq-progress'
  prog.textContent = q.done ? '✓ Accompli' : `${Math.min(q.n, q.target)} / ${q.target}`

  row.append(top, bar, prog)
  return row
}

/** Ligne d'un DÉFI DE CARTE (22/07) : libellé + récompense (loot, pas des pièces), état binaire
 *  « À relever » / « ✓ Accompli » (pas de barre : un défi n'a pas de progression partielle). */
function challengeRow(ch: ChallengeDto): HTMLElement {
  const row = document.createElement('div')
  row.className = ch.done ? 'hq-row hq-row-done' : 'hq-row'

  const top = document.createElement('div')
  top.className = 'hq-row-top'
  const label = document.createElement('span')
  label.className = 'hq-label'
  label.textContent = ch.label
  const gain = document.createElement('span')
  gain.className = 'hq-gain'
  gain.textContent = `🎁 ${ch.rewardName}`
  top.append(label, gain)

  const prog = document.createElement('div')
  prog.className = 'hq-progress'
  prog.textContent = ch.done ? '✓ Accompli' : 'À relever !'

  row.append(top, prog)
  return row
}

/** Ouvre le panneau Quêtes (modale DOM au-dessus de tout). Charge l'état via apiMineState puis
 *  liste les DÉFIS de cartes (en tête : rares et spéciaux) puis les quêtes par période. Se ferme
 *  au ✕, au clic sur le fond, ou par Échap — en retirant ses écouteurs (pas d'empilement de
 *  handlers si on le rouvre). Dégrade en silence hors ligne. */
export function openQuests(mePseudo?: string): void {
  if (document.querySelector('.hq-overlay')) return // déjà ouvert : un seul panneau à la fois

  const overlay = document.createElement('div')
  overlay.className = 'hq-overlay'
  const card = document.createElement('div')
  card.className = 'hq-card'
  card.setAttribute('role', 'dialog')
  card.setAttribute('aria-label', 'Quêtes')

  const head = document.createElement('div')
  head.className = 'hq-head'
  const kicker = document.createElement('p')
  kicker.className = 'hq-kicker'
  kicker.textContent = 'Quêtes'
  const closeBtn = document.createElement('button')
  closeBtn.type = 'button'
  closeBtn.className = 'hq-close'
  closeBtn.setAttribute('aria-label', 'Fermer les quêtes')
  closeBtn.textContent = '✕'
  head.append(kicker, closeBtn)

  const body = document.createElement('div')
  body.className = 'hq-body'
  const status = document.createElement('p')
  status.className = 'hq-status'
  status.textContent = 'Chargement…'
  body.appendChild(status)

  card.append(head, body)
  overlay.appendChild(card)

  function close(): void {
    removeEventListener('keydown', onKey)
    overlay.remove()
  }
  function onKey(e: KeyboardEvent): void {
    if (e.code === 'Escape') { e.preventDefault(); close() }
  }
  addEventListener('keydown', onKey)
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close() })
  closeBtn.addEventListener('click', close)
  document.body.appendChild(overlay)

  apiMineState()
    .then((state: MineStateResponse) => {
      if (!document.body.contains(overlay)) return // fermé avant l'arrivée de la réponse
      body.textContent = ''
      const coins = document.createElement('p')
      coins.className = 'hq-coins'
      coins.textContent = `🪙 ${state.wallet.coins} pièces`
      body.appendChild(coins)
      // DUELS (25/07) : montés en tête, en asynchrone (leur propre requête) — un défi nommément
      // adressé passe avant tout le reste, et son chargement ne retarde pas l'affichage des quêtes.
      if (mePseudo) mountDuelSection(body, mePseudo)
      // Défis de cartes EN TÊTE (récompense = objet, plus rare que des pièces). `?? []` : un
      // serveur pas encore à jour ne renvoie pas `challenges` -> section simplement absente.
      const challenges = state.challenges ?? []
      if (challenges.length > 0) {
        const sec = document.createElement('div')
        sec.className = 'hq-section'
        const t = document.createElement('p')
        t.className = 'hq-section-title'
        t.textContent = 'Défis'
        sec.appendChild(t)
        for (const ch of challenges) sec.appendChild(challengeRow(ch))
        body.appendChild(sec)
      }
      for (const { period, title } of PERIOD_TITLES) {
        const quests = state.quests.filter((q) => q.period === period)
        if (quests.length === 0) continue
        const sec = document.createElement('div')
        sec.className = 'hq-section'
        const t = document.createElement('p')
        t.className = 'hq-section-title'
        t.textContent = title
        sec.appendChild(t)
        for (const q of quests) sec.appendChild(questRow(q))
        body.appendChild(sec)
      }
    })
    .catch(() => {
      if (!document.body.contains(overlay)) return
      status.textContent = 'Quêtes indisponibles pour le moment.'
    })
}

// --- Toasts empilés (accomplissement de quête, message ponctuel) --------------
// Un seul conteneur, créé à la demande et réutilisé ; chaque toast s'auto-retire par minuterie
// (fondu sortant puis suppression). Zéro dépendance à l'état du jeu.
let toastWrap: HTMLDivElement | null = null
function ensureToastWrap(): HTMLDivElement {
  if (toastWrap && document.body.contains(toastWrap)) return toastWrap
  toastWrap = document.createElement('div')
  toastWrap.className = 'qt-wrap'
  document.body.appendChild(toastWrap)
  return toastWrap
}

/** Affiche un toast éphémère (message libre, sans son). Empilé sous les précédents. */
export function showToast(message: string): void {
  const wrap = ensureToastWrap()
  const toast = document.createElement('div')
  toast.className = 'qt-toast'
  toast.textContent = message
  wrap.appendChild(toast)
  setTimeout(() => {
    toast.classList.add('qt-out')
    setTimeout(() => toast.remove(), 400)
  }, 3200)
}

/** Toasts « Quête accomplie : <label> +<coins> 🪙 » pour les quêtes complétées ce niveau, avec UN
 *  son (réutilise le jingle de l'or ramassé via playEvents — aucun nouveau canal audio, respecte
 *  le muet). Ne fait rien si aucune quête n'a été complétée. */
export function showQuestToasts(completed: { label: string; coins: number }[]): void {
  if (completed.length === 0) return
  playEvents([{ type: 'gold', by: 'runner', cell: 0 }])
  for (const q of completed) showToast(`Quête accomplie : ${q.label} +${q.coins} 🪙`)
}

/** Célébration de la SÉRIE DE JOURS (25/07) : n'arrive qu'une fois par jour, à la première prise
 *  du bonus quotidien (le serveur renvoie `daily` non nul à ce moment-là seulement). C'est ICI que
 *  le joueur apprend que la série existe, donc on la nomme, on montre le gain, et on annonce le
 *  prochain palier — c'est la phrase qui fait revenir demain. */
export function showDailyToasts(daily: DailyGrantDto | null): void {
  if (!daily) return
  playEvents([{ type: 'gold', by: 'runner', cell: 0 }])
  const jour = daily.streak > 1 ? `🔥 ${daily.streak} jours d'affilée !` : '🔥 Jour 1 de ta série !'
  const briques = `${daily.bricks} brique${daily.bricks > 1 ? 's' : ''}`
  showToast(`${jour} +${briques} +${daily.coins} 🪙`)
  if (daily.golden > 0) showToast(`✨ BRIQUE D'OR : un objet épique au minimum t'attend à la Mine !`)
  else {
    // On ne promet que le PROCHAIN palier, jamais toute la grille : une seule raison de revenir,
    // clairement datée, vaut mieux qu'un tableau de récompenses.
    const versBrique = STREAK_BRICK_STEP - (daily.streak % STREAK_BRICK_STEP)
    const versOr = STREAK_GOLDEN_STEP - (daily.streak % STREAK_GOLDEN_STEP)
    if (versOr <= versBrique) showToast(`Encore ${versOr} jour${versOr > 1 ? 's' : ''} d'affilée pour une brique d'or.`)
    else showToast(`Reviens ${versBrique} jour${versBrique > 1 ? 's' : ''} de suite pour 2 briques.`)
  }
}

/** Toasts de DUEL DIFFÉRÉ remporté (25/07) : le serveur ne les renvoie qu'à la résolution (temps
 *  strictement meilleur que celui du défiant), donc jamais deux fois pour le même duel. */
export function showDuelToasts(won: ScoreResponse['duelsWon']): void {
  if (!won || won.length === 0) return
  playEvents([{ type: 'gold', by: 'runner', cell: 0 }])
  for (const d of won) {
    showToast(`⚔️ Défi de ${d.other} RELEVÉ sur « ${d.title} » !`)
  }
}

/** Toasts de DÉFI DE CARTE accompli (22/07) : le serveur ne les renvoie qu'à la PREMIÈRE
 *  complétion (POST /api/scores -> 200), donc jamais de re-toast en refaisant le temps. Deux
 *  lignes : l'exploit, puis la récompense (« déjà à toi » si le loot avait été tiré à la Mine). */
export function showChallengeToasts(completed: ScoreResponse['challengesCompleted']): void {
  if (completed.length === 0) return
  playEvents([{ type: 'gold', by: 'runner', cell: 0 }])
  for (const ch of completed) {
    showToast(`🏆 Défi accompli : ${ch.label}`)
    showToast(ch.alreadyOwned ? `Récompense : ${ch.rewardName} (déjà à toi)` : `🎁 ${ch.rewardName} débloqué !`)
  }
}
